import { Link, createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { Input } from '@/components/ui/input'
import { useModelsList } from '@/features/docs/hooks/useGenerationsDocs'
import { ModelFamilyIcon } from '@/shared/components/icons/ModelFamilyIcon'
import { getAdjacentItems } from '@/features/docs/config/navigation'
import { DocsBreadcrumb } from '@/features/docs/components/DocsBreadcrumb'
import { DocsCopyButton } from '@/features/docs/components/DocsCopyButton'
import { generateLinks, generateMetaTags } from '@/lib/seo'
import { getBreadcrumbSchema, renderJsonLd } from '@/lib/structured-data'

const SLUG = 'api-reference/generation-models'
const TITLE = 'Model Catalog'

export const Route = createFileRoute('/docs/api-reference/generation-models')({
  head: () => ({
    meta: generateMetaTags({
      title: `${TITLE} - ModelStack Docs`,
      description:
        'Browse every AI model available for image and video generation on ModelStack, with pricing and capabilities for each.',
      canonical: `/docs/${SLUG}`,
    }),
    links: generateLinks({ canonical: `/docs/${SLUG}` }),
    scripts: [
      {
        type: 'application/ld+json',
        children: renderJsonLd(
          getBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Docs', url: '/docs' },
            { name: TITLE, url: `/docs/${SLUG}` },
          ]) as any,
        ),
      },
    ],
  }),
  component: ModelsCatalogPage,
})

function ModelsCatalogPage() {
  const { data, loading, error } = useModelsList()
  const [search, setSearch] = useState('')
  const [selectedFamily, setSelectedFamily] = useState<string | null>(null)
  const { prev, next } = getAdjacentItems(SLUG)

  const families = data?.families ?? []

  const filtered = useMemo(() => {
    if (!data) return []
    return data.models.filter((model) => {
      const q = search.toLowerCase()
      const matchesSearch = q.length === 0 ||
        model.displayName.toLowerCase().includes(q) ||
        model.id.toLowerCase().includes(q)
      const matchesFamily = !selectedFamily || model.family === selectedFamily
      return matchesSearch && matchesFamily
    })
  }, [search, selectedFamily, data])

  return (
    <div className="flex gap-0">
      <div className="flex-1 min-w-0 px-8 py-10 max-w-3xl">
        <DocsBreadcrumb slug={SLUG} />

        <div className="flex items-start justify-between gap-4 mb-2">
          <h1 className="text-3xl font-bold text-[var(--color-text-primary)] tracking-[-0.03em] md:text-4xl">
            Model Catalog
          </h1>
          <DocsCopyButton slug={SLUG} />
        </div>
        <p className="text-[var(--color-text-secondary)] text-lg mb-8">
          Browse all {data?.total ?? '...'} supported AI models for image and video generation
        </p>

        <div className="space-y-4 mb-8">
          <Input
            placeholder="Search models..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-md"
          />

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedFamily(null)}
              className={`px-4 py-2 rounded-full border text-sm font-medium transition-all duration-300 ease-brisk active:scale-[0.97] ${
                selectedFamily === null
                  ? 'bg-[var(--color-primary)] text-[var(--color-primary-foreground)] border-[var(--color-primary)] shadow-[0_0_24px_-8px_var(--color-primary-shadow)]'
                  : 'border-[var(--color-border)] hover:border-[var(--color-primary)]/40 hover:bg-[var(--color-control-surface-hover)] hover:-translate-y-0.5 text-[var(--color-text-secondary)]'
              }`}
            >
              All Families
            </button>
            {families.map((family) => (
              <button
                key={family}
                onClick={() => setSelectedFamily(family)}
                className={`px-4 py-2 rounded-full border text-sm font-medium capitalize transition-all duration-300 ease-brisk active:scale-[0.97] ${
                  selectedFamily === family
                    ? 'bg-[var(--color-primary)] text-[var(--color-primary-foreground)] border-[var(--color-primary)] shadow-[0_0_24px_-8px_var(--color-primary-shadow)]'
                    : 'border-[var(--color-border)] hover:border-[var(--color-primary)]/40 hover:bg-[var(--color-control-surface-hover)] hover:-translate-y-0.5 text-[var(--color-text-secondary)]'
                }`}
              >
                {family}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="grid gap-6 md:grid-cols-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="border border-[var(--color-border)] rounded-2xl p-6 space-y-4 animate-pulse">
                <div className="h-6 bg-[var(--color-bg-tertiary)] rounded-full w-3/4" />
                <div className="h-4 bg-[var(--color-bg-tertiary)] rounded-full w-1/2" />
                <div className="h-4 bg-[var(--color-bg-tertiary)] rounded-full w-full" />
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="text-center py-12 text-[var(--color-error)]">
            <p>Failed to load models: {error}</p>
          </div>
        )}

        {!loading && !error && (
          <>
            <h2 className="sr-only">Available generation models</h2>
            <div className="grid gap-6 md:grid-cols-2">
              {filtered.map((model) => (
                <ModelCard key={model.id} model={model} />
              ))}
            </div>
          </>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="text-center py-12 text-[var(--color-text-secondary)]">
            <p>No models found matching your criteria</p>
          </div>
        )}

        {/* Prev/Next navigation */}
        {(prev || next) && (
          <div className="flex items-center justify-between gap-4 mt-12 pt-8 border-t border-[var(--color-border)]">
            {prev ? (
              <Link
                // @ts-expect-error - Dynamic doc routes not in generated types
                to={`/docs/${prev.slug}`}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-[var(--color-border)] hover:border-[var(--color-primary)]/40 hover:bg-[var(--color-bg-secondary)]/70 hover:backdrop-blur-md hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-24px_var(--color-shadow)] transition-all duration-300 ease-brisk group max-w-[45%]"
              >
                <span className="material-symbols-outlined text-lg text-[var(--color-text-tertiary)] group-hover:text-[var(--color-primary)] shrink-0">
                  arrow_back
                </span>
                <div className="text-left min-w-0">
                  <div className="text-xs text-[var(--color-text-tertiary)] mb-0.5">Previous</div>
                  <div className="text-sm font-medium text-[var(--color-text-primary)] group-hover:text-[var(--color-primary)] truncate">
                    {prev.title}
                  </div>
                </div>
              </Link>
            ) : (
              <div />
            )}
            {next && (
              <Link
                // @ts-expect-error - Dynamic doc routes not in generated types
                to={`/docs/${next.slug}`}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-[var(--color-border)] hover:border-[var(--color-primary)]/40 hover:bg-[var(--color-bg-secondary)]/70 hover:backdrop-blur-md hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-24px_var(--color-shadow)] transition-all duration-300 ease-brisk group ml-auto max-w-[45%]"
              >
                <div className="text-right min-w-0">
                  <div className="text-xs text-[var(--color-text-tertiary)] mb-0.5">Next</div>
                  <div className="text-sm font-medium text-[var(--color-text-primary)] group-hover:text-[var(--color-primary)] truncate">
                    {next.title}
                  </div>
                </div>
                <span className="material-symbols-outlined text-lg text-[var(--color-text-tertiary)] group-hover:text-[var(--color-primary)] shrink-0">
                  arrow_forward
                </span>
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Right ToC rail */}
      <div className="hidden xl:block w-56 shrink-0 px-4 py-10">
        <nav className="sticky top-24 space-y-3">
          <p className="px-3 text-xs font-semibold uppercase tracking-widest text-[var(--color-text-tertiary)]">
            On this page
          </p>
          <ul className="space-y-0.5 text-sm">
            <li>
              <a href="#family-list" className="block rounded-full px-3 py-1.5 text-[var(--color-text-secondary)] transition-all duration-300 ease-brisk hover:bg-[var(--color-control-surface-hover)] hover:text-[var(--color-text-primary)]">
                Search & Filter
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  )
}

function ModelCard({ model }: { model: NonNullable<ReturnType<typeof useModelsList>['data']>['models'][0] }) {
  return (
    <div className="group rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-secondary)]/70 backdrop-blur-md p-6 space-y-4 transition-all duration-300 ease-brisk hover:border-[var(--color-primary)]/40 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-24px_var(--color-shadow)]">
      <div>
        <h3 className="text-xl font-bold tracking-[-0.02em] text-[var(--color-text-primary)] transition-colors duration-300 ease-brisk group-hover:text-[var(--color-primary)]">
          {model.displayName}
        </h3>
        <p className="text-sm text-[var(--color-text-secondary)] font-mono">{model.id}</p>
      </div>

      <div className="flex items-center gap-3 text-sm">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[var(--color-bg-tertiary)] rounded-full capitalize text-[var(--color-text-secondary)]">
          <ModelFamilyIcon modelFamily={model.family as Parameters<typeof ModelFamilyIcon>[0]['modelFamily']} size={14} />
          {model.family}
        </span>
        <span className="text-[var(--color-text-secondary)]">
          from ${(model.minPrice / 100).toFixed(2)}
        </span>
      </div>

      <p className="text-xs text-[var(--color-text-secondary)]">
        {model.variantCount} generation type{model.variantCount !== 1 ? 's' : ''}
      </p>

      <div className="flex flex-wrap gap-2">
        {model.variantLabels.slice(0, 4).map((label, i) => (
          <span
            key={model.variantNames[i]}
            title={model.variantNames[i]}
            className="text-xs px-2.5 py-1 rounded-full border border-[var(--color-border)] text-[var(--color-text-secondary)]"
          >
            {label}
          </span>
        ))}
        {model.variantLabels.length > 4 && (
          <span className="text-xs text-[var(--color-text-secondary)]">
            +{model.variantLabels.length - 4} more
          </span>
        )}
      </div>
    </div>
  )
}
